/**
 * @file src/pages/payment-management/logic/stats.js
 * @description Pure math for Payment Management page summary stats.
 *
 * No React, no state, no side effects. Easy to unit test.
 */

/**
 * Sum the `amount` field across a list of payments.
 *
 * @param {Array<{ amount?: number | string }>} payments
 * @returns {number}
 */
export const sumAmount = (payments) =>
  payments.reduce((total, p) => total + (parseFloat(p.amount) || 0), 0);

/**
 * Count payments whose `paymentDate` falls in the current calendar month
 * and year (per the user's local clock).
 *
 * @param {Array<{ paymentDate?: string }>} payments
 * @returns {number}
 */
export const countThisMonth = (payments) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return payments.filter((p) => {
    const d = new Date(p.paymentDate);
    return d.getFullYear() === year && d.getMonth() === month;
  }).length;
};

/**
 * Build a `{ mode: count }` map for a fixed list of payment modes.
 * Modes with 0 payments are still present in the result (set to 0)
 * so the caller can decide whether to render or hide them.
 *
 * @param {Array<{ paymentMode?: string }>} payments
 * @param {string[]} modes - All possible payment modes
 * @returns {Record<string, number>}
 */
export const countByMode = (payments, modes) =>
  modes.reduce((acc, mode) => {
    acc[mode] = payments.filter((p) => p.paymentMode === mode).length;
    return acc;
  }, {});

/**
 * The single mode with the highest payment count, or "—" if no payments.
 * Ties resolved arbitrarily (whichever comes first in `modeCounts`).
 *
 * @param {Record<string, number>} modeCounts
 * @returns {string}
 */
export const findTopMode = (modeCounts) => {
  const entries = Object.entries(modeCounts);
  if (entries.length === 0) return "—";

  const [topMode, topCount] = entries.reduce(
    (best, current) => (current[1] > best[1] ? current : best),
    entries[0],
  );

  return topCount === 0 ? "—" : topMode;
};
